import Skeleton from '@mui/material/Skeleton';
import moment from 'moment';
import { type JSX, useMemo, useState } from 'react';

import { DashboardApi } from 'Api/Dashboards/DashboardApi';
import { GroupedBarsChart } from 'Components/BaseCharts/GroupedBarsChart';
import { ChartAxis } from 'Components/BaseCharts/charts.common';
import { CSSColors } from 'Components/Colors';
import { FormFieldDateOptionSelect } from 'Components/FormField/FormFieldDateOptionSelect/FormFieldDateOptionSelect';
import { ChangeEventType } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldSelectDark } from 'Components/FormField/FormFieldSelectDark/FormFieldSelectDark';
import { CustomDateRangeModal } from 'Components/Modal/CustomDateRangeModal/CustomDateRangeModal';
import { Text } from 'Components/Text/Text';
import { useDateFilter } from 'Hooks/DateFilter';
import { DashboardRiskOptions, DateOptionSelectOption, DefaultPastDateRangeOptions, DueDiligenceDashboardRiskType, isValidDashboardRiskType } from 'Models/Dashboards';
import { effectivenessAsString } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { riskRatingAsString } from 'Models/RiskRating';
import { getMonths } from 'Pages/Dashboards/Helpers/Dashboards.helpers';

import { AssessmentSubjectsDisplayModal, SelectedAssessmentSubjectDetails } from '../AssessmentSubjectsDisplayModal/AssessmentSubjectsDisplayModal';
import { AssessmentsBarChartDataRow, buildAssessmentsBarChartByMonth, getPercentageCompletedOnTime, onlyMostRecentAssessmentsPerMonth } from '../DueDiligenceDashboard.helpers';
import { useCompletedRiskAssessmentsQuery } from '../DueDiligenceDashboard.hooks';
import styles from '../DueDiligenceDashboard.module.css';

enum Modals {
    NONE,
    DISPLAY_ASSESSMENT_SUBJECTS,
    CUSTOM_DATE,
}

interface AssessmentSubjectDataItem {
    monthKey: string;
    riskScore: number | undefined;
    completedOnTime: boolean;
    assessmentSubjectId: string;
    assessmentSubjectName: string;
    organizationId: string;
    organizationName: string;
}

interface CompletedAssessmentsChartData {
    assessmentDataRows: AssessmentsBarChartDataRow[];
    xAxisLabels: string[];
    yAxisInfo: ChartAxis[];
    line: AssessmentsBarChartDataRow;
    assessmentSubjectsData: AssessmentSubjectDataItem[];
}

interface CompletedAssessmentsChartProps {
    dashboardApi: DashboardApi;
    module: DueDiligenceModule;
}

export const CompletedAssessmentsChart = (props: CompletedAssessmentsChartProps): JSX.Element => {
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);
    const [assessmentSubjectsModalSubtitle, setAssessmentSubjectsModalSubtitle] = useState<string>();
    const [selectedAssessmentSubjects, setSelectedAssessmentSubjects] = useState<SelectedAssessmentSubjectDetails[]>();
    const [riskType, setRiskType] = useState<DueDiligenceDashboardRiskType>(DueDiligenceDashboardRiskType.RESIDUAL);
    const dateFilterHookValues = useDateFilter(DateOptionSelectOption.LAST_THREE_MONTHS);
    const { completedRiskAssessments, completedRiskAssessmentsError } = useCompletedRiskAssessmentsQuery(props.dashboardApi, props.module, dateFilterHookValues.selection.dateRange);

    const completedAssessmentsChartData = useMemo<CompletedAssessmentsChartData | undefined>(() => {
        if (completedRiskAssessments) {
            const completedAssessmentsData = onlyMostRecentAssessmentsPerMonth(completedRiskAssessments).map((assessment) => ({
                monthKey: moment(assessment.completed_time).format('MMM YY'),
                riskScore: (() => {
                    switch (riskType) {
                        case DueDiligenceDashboardRiskType.INHERENT:
                            return assessment.inherent_risk_score;
                        case DueDiligenceDashboardRiskType.CONTROL_EFFECTIVENESS:
                            return assessment.control_effectiveness;
                        case DueDiligenceDashboardRiskType.RESIDUAL:
                        default:
                            return assessment.residual_risk_score;
                    }
                })(),
                completedOnTime: assessment.completed_on_time,
                assessmentSubjectId: assessment.service_id,
                assessmentSubjectName: assessment.service_name,
                organizationId: assessment.third_party_id,
                organizationName: assessment.third_party_name,
            }));

            const months = getMonths(dateFilterHookValues.selection.dateRange.startDate, dateFilterHookValues.selection.dateRange.endDate);

            const assessmentDataRows = buildAssessmentsBarChartByMonth(completedAssessmentsData, riskType, months);
            const yAxisInfo: ChartAxis[] = [
                { name: props.module === ProductModule.UCRM ? 'Number of Use Cases' : 'Number of Services', position: 'left', minInterval: 1 },
                { name: 'Percent Completed on Time', position: 'right', minInterval: 1, max: 100 },
            ];

            return { assessmentDataRows, xAxisLabels: months, yAxisInfo, line: { name: 'Percent Completed on Time', data: getPercentageCompletedOnTime(completedAssessmentsData, months), color: CSSColors.LIGHT_BLUE }, assessmentSubjectsData: completedAssessmentsData };
        }
    }, [completedRiskAssessments, riskType, dateFilterHookValues, props.module]);

    const handleChartClick = (item: string): void => {
        const clickTargetDetails = item.split(' | ');
        const targetRating = clickTargetDetails[1];
        const targetDate = clickTargetDetails[0];

        if (targetRating && targetRating !== 'Percent Completed on Time' && targetDate && completedRiskAssessments && completedAssessmentsChartData?.assessmentSubjectsData) {
            setAssessmentSubjectsModalSubtitle(`${targetRating} - ${targetDate}`);
            setModalState(Modals.DISPLAY_ASSESSMENT_SUBJECTS);
            const selectedAssessmentSubjects = completedAssessmentsChartData.assessmentSubjectsData
                .filter((assessmentSubject) => {
                    if (assessmentSubject.monthKey.toUpperCase() === targetDate) {
                        if (riskType === DueDiligenceDashboardRiskType.CONTROL_EFFECTIVENESS) {
                            if (assessmentSubject.riskScore !== undefined && effectivenessAsString(assessmentSubject.riskScore) === targetRating) {
                                return assessmentSubject;
                            } else {
                                return null;
                            }
                        } else if (assessmentSubject.riskScore !== undefined && riskRatingAsString(assessmentSubject.riskScore) === targetRating) {
                            return assessmentSubject;
                        } else {
                            return null;
                        }
                    } else {
                        return null;
                    }
                })
                .map((assessmentSubject) => ({
                    assessmentSubjectId: assessmentSubject.assessmentSubjectId,
                    assessmentSubjectName: assessmentSubject.assessmentSubjectName,
                    organizationId: assessmentSubject.organizationId,
                    organizationName: assessmentSubject.organizationName,
                }));
            setSelectedAssessmentSubjects(selectedAssessmentSubjects);
        }
    };

    const handleAssessmentSubjectsModalClose = () => {
        setModalState(Modals.NONE);
        setAssessmentSubjectsModalSubtitle(undefined);
        setSelectedAssessmentSubjects(undefined);
    };

    const handleRiskTypeChange = (value: ChangeEventType) => {
        if (value && isValidDashboardRiskType(value)) {
            setRiskType(value);
        }
    };

    if (completedRiskAssessmentsError) {
        return (
            <div className={styles.cellHeader}>
                <Text variant="Header2" color="white">
                    Completed Due Diligence
                </Text>
                <Text color="white">{completedRiskAssessmentsError}</Text>
            </div>
        );
    }

    return (
        <>
            {modalState === Modals.CUSTOM_DATE && <CustomDateRangeModal datesRestriction="pastDatesOnly" hideModal={() => setModalState(Modals.NONE)} dateFilterHookValues={dateFilterHookValues} />}
            {modalState === Modals.DISPLAY_ASSESSMENT_SUBJECTS && assessmentSubjectsModalSubtitle && selectedAssessmentSubjects && <AssessmentSubjectsDisplayModal hideModal={handleAssessmentSubjectsModalClose} module={props.module} subtitle={assessmentSubjectsModalSubtitle} selectedAssessmentSubjects={selectedAssessmentSubjects} />}
            <div className={styles.cellHeader}>
                <Text variant="Header2" color="white">
                    Completed Due Diligence
                </Text>
                <div className={styles.filters}>
                    <FormFieldSelectDark formFieldId="riskType" formFieldLabel="Risk Type" options={DashboardRiskOptions} selectedOption={riskType} handleChange={handleRiskTypeChange} />
                    <FormFieldDateOptionSelect formFieldId="date" options={DefaultPastDateRangeOptions} onSelectCustom={() => setModalState(Modals.CUSTOM_DATE)} dateFilterHookValues={dateFilterHookValues} />
                </div>
            </div>
            {completedAssessmentsChartData ? <GroupedBarsChart groupingMode="stacked" onChartClick={handleChartClick} xAxisLabels={completedAssessmentsChartData.xAxisLabels} barsSeriesArray={completedAssessmentsChartData.assessmentDataRows} yAxisInfo={completedAssessmentsChartData.yAxisInfo} line={completedAssessmentsChartData.line} /> : <Skeleton variant="rounded" width={'100%'} height={300} />}
        </>
    );
};
